import { Controller } from "stimulus"
import { sendEmailQueue } from "../lib/email_send_queue"

export default class extends Controller {
  static targets = [
    "search",
    "row",
    "selectedCount",
    "selectedInputs",
    "selectedPanel",
    "selectedPanelCount",
    "selectedList",
    "list",
    "rowsContainer",
    "loading",
    "picker",
    "pickerClose",
    "submit",
    "replaySubmit",
    "progress",
    "currentRecipient",
    "progressSummary",
    "failures",
    "cancel"
  ]

  connect() {
    this.page = 0
    this.loading = false
    this.hasMore = this.rowTargets.length >= this.pageSize
    this.selectedIds = new Set()
    this.selectedUsers = new Map()
    this.searchTimer = null
    this.sending = false
    this.cancelRequested = false
    this.handleKeydown = this.handleKeydown.bind(this)
    this.beforeUnload = (event) => {
      if (!this.sending) return
      event.preventDefault()
      event.returnValue = ""
    }
    this.beforeVisit = (event) => {
      if (!this.sending) return
      event.preventDefault()
      window.alert("Email sending is in progress. Stop sending before leaving this page.")
    }
    document.addEventListener("turbolinks:before-visit", this.beforeVisit)
    this.syncRowsFromSelection()
    this.updateCount()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    window.removeEventListener("beforeunload", this.beforeUnload)
    document.removeEventListener("turbolinks:before-visit", this.beforeVisit)
    document.body.classList.remove("events-invite-picker-open")
  }

  openPicker() {
    if (!this.hasPickerTarget) return

    this.pickerTarget.classList.add("events-admin-invitations__picker--open")
    document.body.classList.add("events-invite-picker-open")
    document.addEventListener("keydown", this.handleKeydown)
    if (this.hasSearchTarget) this.searchTarget.focus()
  }

  closePicker() {
    if (!this.hasPickerTarget || this.sending) return

    this.pickerTarget.classList.remove("events-admin-invitations__picker--open")
    document.body.classList.remove("events-invite-picker-open")
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.closePicker()
  }

  filter() {
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      this.loadUsers({ reset: true })
    }, 250)
  }

  clear() {
    this.selectedIds.clear()
    this.selectedUsers.clear()
    this.syncRowsFromSelection()
    this.updateCount()
  }

  async submitQueue(event) {
    if (this.sending || this.selectedIds.size === 0) return

    event.preventDefault()
    await this.runQueue(event.currentTarget, Array.from(this.selectedIds))
  }

  submitReplay() {
    if (this.sending || this.selectedIds.size === 0) return
    const form = document.getElementById("additional-replay-form")
    if (!form) return

    form.querySelectorAll("[data-replay-recipient]").forEach((input) => input.remove())
    this.selectedIds.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "user_ids[]"
      input.value = id
      input.setAttribute("data-replay-recipient", "true")
      form.appendChild(input)
    })
    form.requestSubmit()
  }

  cancelQueue() {
    this.cancelRequested = true
    if (this.hasProgressSummaryTarget) this.progressSummaryTarget.textContent = "Stopping after the current email…"
  }

  changeEvent(event) {
    this.selectedIds.clear()
    this.selectedUsers.clear()
    this.updateCount()
    event.currentTarget.form.submit()
  }

  toggleUser(event) {
    const checkbox = event.currentTarget
    const userId = checkbox.value

    if (checkbox.checked) {
      this.selectedIds.add(userId)
      const row = checkbox.closest("[data-user-id]")
      this.selectedUsers.set(userId, {
        name: row.dataset.userName,
        email: row.dataset.userEmail
      })
    } else {
      this.selectedIds.delete(userId)
      this.selectedUsers.delete(userId)
    }

    this.updateCount()
  }

  removeSelected(event) {
    const userId = event.currentTarget.dataset.userId
    this.selectedIds.delete(userId)
    this.selectedUsers.delete(userId)
    this.syncRowsFromSelection()
    this.updateCount()
  }

  scroll() {
    if (!this.hasMore || this.loading || !this.hasListTarget) return

    const scrollLevel = this.listTarget.scrollTop / (this.listTarget.scrollHeight - this.listTarget.clientHeight)
    if (scrollLevel > 0.8) this.loadUsers()
  }

  updateCount() {
    this.syncHiddenInputs()
    this.updateSelectedPanel()

    if (!this.hasSelectedCountTarget) return

    this.selectedCountTarget.textContent = `${this.selectedIds.size} selected`
    if (this.hasSubmitTarget && !this.sending) this.submitTarget.disabled = this.selectedIds.size === 0
    if (this.hasReplaySubmitTarget && !this.sending) this.replaySubmitTarget.disabled = this.selectedIds.size === 0
  }

  async runQueue(form, recipientIds) {
    if (form.dataset.missingLinkMessage) {
      window.alert(form.dataset.missingLinkMessage)
      return
    }

    const formEntries = Array.from(new FormData(form).entries())
    this.sending = true
    document.body.classList.add("email-queue-active")
    this.cancelRequested = false
    this.queueCounts = { sent: 0, skipped: 0, failed: 0, unknown: 0 }
    this.failuresTarget.innerHTML = ""
    this.setQueueControlsDisabled(true)
    this.progressTarget.closest("[data-email-queue-status]").hidden = false
    this.renderQueueProgress(recipientIds.length)
    window.addEventListener("beforeunload", this.beforeUnload)

    await sendEmailQueue({
      form,
      recipientIds,
      formEntries,
      cancelled: () => this.cancelRequested,
      onStart: (current) => this.renderCurrentRecipient(current),
      onResult: (result) => {
        const status = Object.prototype.hasOwnProperty.call(this.queueCounts, result.status) ? result.status : "unknown"
        this.queueCounts[status] += 1
        if (status === "failed" || status === "unknown") this.renderQueueFailure(result, status)
        this.renderQueueProgress(recipientIds.length)
      }
    })

    this.sending = false
    document.body.classList.remove("email-queue-active")
    window.removeEventListener("beforeunload", this.beforeUnload)
    this.setQueueControlsDisabled(false)
    const processed = Object.values(this.queueCounts).reduce((sum, count) => sum + count, 0)
    this.progressTarget.textContent = this.cancelRequested ? `Stopped after ${processed} of ${recipientIds.length}` : `Finished processing ${processed} emails`
    this.currentRecipientTarget.textContent = this.cancelRequested ? "Sending stopped." : "All selected emails have been processed."
    if (this.queueCounts.failed > 0 || this.queueCounts.unknown > 0) {
      this.progressSummaryTarget.textContent += ". Failed or uncertain recipients remain available after reloading."
    } else if (!this.cancelRequested) {
      window.setTimeout(() => window.location.reload(), 1000)
    }
  }

  renderQueueFailure(result, status) {
    const user = this.selectedUsers.get(result.recipientId)
    const email = (result.payload && result.payload.recipient_email) || (user && user.email)
    const item = document.createElement("li")
    item.textContent = `${email || `Recipient ${result.recipientId}`}: ${status === "failed" ? "not sent" : "delivery uncertain"}`
    this.failuresTarget.append(item)
  }

  renderCurrentRecipient({ recipientId, index, total }) {
    const user = this.selectedUsers.get(recipientId)
    this.currentRecipientTarget.textContent = `Sending ${index} of ${total}${user && user.email ? ` to ${user.email}` : ""}…`
  }

  renderQueueProgress(total) {
    const processed = Object.values(this.queueCounts).reduce((sum, count) => sum + count, 0)
    this.progressTarget.textContent = `Emails are being sent — ${processed} of ${total} processed`
    this.progressSummaryTarget.textContent = `${this.queueCounts.sent} sent · ${this.queueCounts.skipped} skipped · ${this.queueCounts.failed} failed · ${this.queueCounts.unknown} uncertain`
  }

  setQueueControlsDisabled(disabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = disabled
    if (this.hasReplaySubmitTarget) this.replaySubmitTarget.disabled = disabled
    if (this.hasCancelTarget) this.cancelTarget.hidden = !disabled
    if (this.hasSearchTarget) this.searchTarget.disabled = disabled
    this.rowTargets.forEach((row) => { this.checkboxFor(row).disabled = disabled })
    if (this.hasPickerCloseTarget) this.pickerCloseTarget.disabled = disabled
    const resendCheckbox = document.getElementById("resend_existing")
    if (resendCheckbox) resendCheckbox.disabled = disabled
  }

  checkboxFor(row) {
    return row.querySelector("input[type='checkbox']")
  }

  selectedCount() {
    return this.selectedIds.size
  }

  loadUsers({ reset = false } = {}) {
    if (!this.hasListTarget || !this.hasRowsContainerTarget) return

    this.loading = true
    this.showLoading()

    const nextPage = reset ? 0 : this.page + 1
    const url = new URL(this.listTarget.dataset.url, window.location.origin)
    url.searchParams.set("page", nextPage)
    url.searchParams.set("user_search", this.searchTarget.value.trim())

    fetch(url.toString(), { headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" } })
      .then((response) => {
        if (!response.ok) throw new Error(`Invitation users request failed with ${response.status}`)
        return response.text()
      })
      .then((html) => {
        if (reset) {
          this.rowsContainerTarget.innerHTML = html
          this.page = 0
          this.listTarget.scrollTop = 0
        } else {
          this.rowsContainerTarget.insertAdjacentHTML("beforeend", html)
          this.page = nextPage
        }

        this.hasMore = html.trim() !== ""
        this.syncRowsFromSelection()
        this.updateCount()
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        this.loading = false
        this.hideLoading()
      })
  }

  syncRowsFromSelection() {
    this.rowTargets.forEach((row) => {
      this.checkboxFor(row).checked = this.selectedIds.has(row.dataset.userId)
    })
  }

  syncHiddenInputs() {
    if (!this.hasSelectedInputsTarget) return

    this.selectedInputsTarget.innerHTML = Array.from(this.selectedIds)
      .map((id) => `<input type="hidden" name="user_ids[]" value="${id}">`)
      .join("")
  }

  updateSelectedPanel() {
    if (!this.hasSelectedPanelTarget || !this.hasSelectedListTarget) return

    this.selectedPanelTarget.hidden = this.selectedIds.size === 0
    if (this.hasSelectedPanelCountTarget) {
      this.selectedPanelCountTarget.textContent = this.selectedIds.size
    }

    this.selectedListTarget.innerHTML = ""
    this.selectedIds.forEach((id) => {
      const user = this.selectedUsers.get(id)
      if (!user) return

      const item = document.createElement("div")
      item.className = "events-admin-invitations__selected-user"

      const details = document.createElement("span")
      const name = document.createElement("strong")
      const email = document.createElement("small")
      name.textContent = user.name
      email.textContent = user.email
      details.append(name, email)

      const removeButton = document.createElement("button")
      removeButton.type = "button"
      removeButton.className = "events-admin-invitations__selected-remove"
      removeButton.dataset.action = "click->event-invitations#removeSelected"
      removeButton.dataset.userId = id
      removeButton.setAttribute("aria-label", `Remove ${user.name} from invitation selection`)
      removeButton.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>'

      item.append(details, removeButton)
      this.selectedListTarget.append(item)
    })
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("d-none")
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("d-none")
  }

  get pageSize() {
    return 50
  }
}
